/**
 * Quest List Panel
 * Left panel of QuestTool showing organized quest list with chains
 * Implements drag-and-drop reordering using dnd-kit
 */

import { useState, useMemo } from 'react';
import {
  DndContext,
  DragEndEvent,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { questChainManager, customQuestManager } from '@/systems';
import { QuestDefinition } from '@/types';
import { QuestChainItem } from './QuestChainItem';
import { SortableQuestCard } from './SortableQuestCard';

interface QuestListPanelProps {
  quests: QuestDefinition[];
  selectedQuestId: string | null;
  onSelectQuest: (questId: string) => void;
  onNewQuest: () => void;
  searchTerm: string;
  onSearchChange: (term: string) => void;
}

export function QuestListPanel({
  quests,
  selectedQuestId,
  onSelectQuest,
  onNewQuest,
  searchTerm,
  onSearchChange,
}: QuestListPanelProps) {
  const [expandedChains, setExpandedChains] = useState<Set<string>>(new Set());
  const [showChainCreator, setShowChainCreator] = useState(false);
  const [newChainName, setNewChainName] = useState('');

  const chains = questChainManager.getAllChains();

  // Filter quests by search term
  const filteredQuests = useMemo(() => {
    if (!searchTerm) return quests;

    const term = searchTerm.toLowerCase();
    return quests.filter(
      q =>
        q.name.toLowerCase().includes(term) ||
        q.description.toLowerCase().includes(term) ||
        q.id.toLowerCase().includes(term)
    );
  }, [quests, searchTerm]);

  // Group quests by chain
  const questsByChain = useMemo(() => {
    const map = new Map<string, QuestDefinition[]>();

    filteredQuests.forEach(quest => {
      const chainId = quest.chainId || 'uncategorized';
      if (!map.has(chainId)) {
        map.set(chainId, []);
      }
      map.get(chainId)!.push(quest);
    });

    // Sort quests within each chain by displayOrder
    map.forEach(quests => {
      quests.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    });

    return map;
  }, [filteredQuests]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 }, // 8px drag threshold
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeQuestId = active.id as string;
    const overQuestId = over.id as string;

    const activeQuest = quests.find(q => q.id === activeQuestId);
    const overQuest = quests.find(q => q.id === overQuestId);

    if (!activeQuest || !overQuest) return;

    const activeChainId = activeQuest.chainId || null;
    const overChainId = overQuest.chainId || null;

    // If moving between chains, update chainId first
    if (activeChainId !== overChainId) {
      customQuestManager.assignQuestToChain(activeQuestId, overChainId);
    }

    // Get the target chain's quests
    const chainQuests = questsByChain.get(overChainId || 'uncategorized') || [];
    const oldIndex = chainQuests.findIndex(q => q.id === activeQuestId);
    const newIndex = chainQuests.findIndex(q => q.id === overQuestId);

    // Reorder quests in the chain (only custom quests can be reordered)
    if (oldIndex !== newIndex) {
      chainQuests.forEach((quest, index) => {
        // Skip default quests - they cannot be reordered
        if (!customQuestManager.isCustomQuest(quest.id)) {
          return;
        }

        let order = index;
        if (quest.id === activeQuestId) {
          order = newIndex;
        } else if (oldIndex < newIndex && index > oldIndex && index <= newIndex) {
          order = index - 1;
        } else if (oldIndex > newIndex && index >= newIndex && index < oldIndex) {
          order = index + 1;
        }

        customQuestManager.updateQuestOrder(quest.id, order);
      });
    }
  };

  const toggleChain = (chainId: string) => {
    setExpandedChains(prev => {
      const next = new Set(prev);
      if (next.has(chainId)) {
        next.delete(chainId);
      } else {
        next.add(chainId);
      }
      return next;
    });
  };

  const handleCreateChain = () => {
    if (!newChainName.trim()) return;

    questChainManager.createChain(newChainName.trim(), {
      icon: '📁',
      color: '#3B82F6',
    });

    setNewChainName('');
    setShowChainCreator(false);
  };

  return (
    <div className="flex flex-col h-full bg-gray-900">
      {/* Header */}
      <div className="p-4 border-b border-gray-700">
        <div className="flex items-center gap-2 mb-3">
          <input
            type="text"
            placeholder="Search quests..."
            value={searchTerm}
            onChange={e => onSearchChange(e.target.value)}
            className="flex-1 bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
          />
        </div>
        <button
          onClick={onNewQuest}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-bold transition-colors mb-2"
        >
          + New Quest
        </button>
        <button
          onClick={() => setShowChainCreator(!showChainCreator)}
          className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded font-bold transition-colors"
        >
          {showChainCreator ? 'Cancel' : '+ New Chain'}
        </button>

        {/* Chain Creator */}
        {showChainCreator && (
          <div className="mt-3 p-3 bg-gray-800 rounded border border-gray-700">
            <input
              type="text"
              placeholder="Chain name..."
              value={newChainName}
              onChange={e => setNewChainName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCreateChain()}
              className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white text-sm mb-2 focus:outline-none focus:border-blue-500"
              autoFocus
            />
            <button
              onClick={handleCreateChain}
              disabled={!newChainName.trim()}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white px-3 py-2 rounded font-semibold text-sm transition-colors"
            >
              Create Chain
            </button>
          </div>
        )}
      </div>

      {/* Quest List with DnD */}
      <div className="flex-1 overflow-y-auto p-4">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          {/* Chains */}
          {chains.map(chain => {
            const chainQuests = questsByChain.get(chain.id) || [];
            const isExpanded = expandedChains.has(chain.id);

            return (
              <QuestChainItem
                key={chain.id}
                chain={chain}
                quests={chainQuests}
                isExpanded={isExpanded}
                onToggle={() => toggleChain(chain.id)}
                selectedQuestId={selectedQuestId}
                onSelectQuest={onSelectQuest}
              />
            );
          })}

          {/* Uncategorized Section */}
          {questsByChain.has('uncategorized') && (
            <div className="mb-4">
              <h3 className="text-gray-400 text-sm font-semibold mb-2 px-2">
                📋 Uncategorized ({questsByChain.get('uncategorized')!.length})
              </h3>
              <SortableContext
                items={questsByChain.get('uncategorized')!.map(q => q.id)}
                strategy={verticalListSortingStrategy}
              >
                {questsByChain.get('uncategorized')!.map(quest => (
                  <SortableQuestCard
                    key={quest.id}
                    quest={quest}
                    isSelected={quest.id === selectedQuestId}
                    onSelect={onSelectQuest}
                  />
                ))}
              </SortableContext>
            </div>
          )}

          {/* Empty State */}
          {filteredQuests.length === 0 && (
            <div className="text-center text-gray-500 py-8">
              {searchTerm ? (
                <>
                  <p>No quests found matching "{searchTerm}"</p>
                  <button
                    onClick={() => onSearchChange('')}
                    className="mt-2 text-blue-400 hover:text-blue-300 text-sm"
                  >
                    Clear search
                  </button>
                </>
              ) : (
                <p>No quests yet. Create your first quest!</p>
              )}
            </div>
          )}
        </DndContext>
      </div>
    </div>
  );
}
